import { ArrowDownUp, Pencil } from "lucide-react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import {
  RentingFeesInfoCell,
  type RentingFeesInfoCellRow,
} from "@/routes/base-data/processes/qpm/_components/renting-fees-info-cell";
import { useMemo, useState, type ReactNode } from "react";

type SortField = "name" | "type" | "recommendedVisits" | "price" | "updatedAt";
type SortDirection = "asc" | "desc";

export type MonitorOverviewRow = {
  id: string;
  name: string;
  type: MonitorType;
  includeInQpmService: boolean;
  recommendedVisits: number;
  price: number;
  updatedAt?: string;
  rentingFees: RentingFeesInfoCellRow[];
  onEdit: () => void;
  trailingAction?: ReactNode;
};

export function MonitorOverviewTable({
  rows,
  monitorType,
  emptyText = t("Keine Monitore hinterlegt"),
}: {
  rows: MonitorOverviewRow[];
  monitorType: MonitorType;
  emptyText?: string;
}) {
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const showRentingColumn = monitorType === MonitorType.IoT;

  const sortedRows = useMemo(
    () =>
      [...rows].sort((left, right) =>
        compareRows(left, right, sortField, sortDirection),
      ),
    [rows, sortDirection, sortField],
  );

  return (
    <div className="overflow-x-auto">
      <Table className="min-w-[980px]">
        <TableHeader>
          <TableRow>
            <SortableHead
              active={sortField === "name"}
              direction={sortDirection}
              onClick={() =>
                updateSort(
                  "name",
                  sortField,
                  sortDirection,
                  setSortField,
                  setSortDirection,
                )
              }
            >
              {t("Name")}
            </SortableHead>
            <SortableHead
              active={sortField === "type"}
              direction={sortDirection}
              onClick={() =>
                updateSort(
                  "type",
                  sortField,
                  sortDirection,
                  setSortField,
                  setSortDirection,
                )
              }
            >
              {t("Typ")}
            </SortableHead>
            <TableHead className="text-center">{t("QPM Service")}</TableHead>
            <SortableHead
              active={sortField === "recommendedVisits"}
              direction={sortDirection}
              className="text-center"
              onClick={() =>
                updateSort(
                  "recommendedVisits",
                  sortField,
                  sortDirection,
                  setSortField,
                  setSortDirection,
                )
              }
            >
              {t("Empfohlene Maßnahmen")}
            </SortableHead>
            <SortableHead
              active={sortField === "price"}
              direction={sortDirection}
              className="text-right"
              onClick={() =>
                updateSort(
                  "price",
                  sortField,
                  sortDirection,
                  setSortField,
                  setSortDirection,
                )
              }
            >
              {t("Preis")}
            </SortableHead>
            {showRentingColumn ? <TableHead>{t("Miete")}</TableHead> : null}
            <SortableHead
              active={sortField === "updatedAt"}
              direction={sortDirection}
              className="text-right"
              onClick={() =>
                updateSort(
                  "updatedAt",
                  sortField,
                  sortDirection,
                  setSortField,
                  setSortDirection,
                )
              }
            >
              {t("Aktualisiert am")}
            </SortableHead>
            <TableHead className="text-end">{t("Aktion")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.name}</TableCell>
              <TableCell>{MonitorTypeUtil.toString(row.type)}</TableCell>
              <TableCell className="text-center">
                <Checkbox
                  className="disabled:opacity-100"
                  checked={row.includeInQpmService}
                  disabled
                />
              </TableCell>
              <TableCell className="text-center">
                {row.recommendedVisits}
              </TableCell>
              <TableCell className="text-right">
                {formatPrice(row.price)}
              </TableCell>
              {showRentingColumn ? (
                <TableCell>
                  <RentingFeesInfoCell rows={row.rentingFees} />
                </TableCell>
              ) : null}
              <TableCell className="text-right">
                {formatDateTime(row.updatedAt)}
              </TableCell>
              <TableCell className="text-end">
                <div className="flex justify-end gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={row.onEdit}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t("Bearbeiten")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {row.trailingAction}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {sortedRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={showRentingColumn ? 8 : 7}
                className="text-muted-foreground h-24 text-center"
              >
                {emptyText}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}

function SortableHead({
  active,
  direction,
  className,
  onClick,
  children,
}: {
  active: boolean;
  direction: SortDirection;
  className?: string;
  onClick: () => void;
  children: string;
}) {
  return (
    <TableHead className={className}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="-ml-3 h-8 px-3"
        onClick={onClick}
      >
        {children}
        <ArrowDownUp
          className={`ml-2 h-3.5 w-3.5 ${active ? "opacity-100" : "opacity-40"}`}
        />
        <span className="sr-only">
          {direction === "asc" ? t("Aufsteigend") : t("Absteigend")}
        </span>
      </Button>
    </TableHead>
  );
}

function updateSort(
  nextField: SortField,
  currentField: SortField,
  currentDirection: SortDirection,
  setField: (field: SortField) => void,
  setDirection: (direction: SortDirection) => void,
) {
  if (nextField === currentField) {
    setDirection(currentDirection === "asc" ? "desc" : "asc");
    return;
  }
  setField(nextField);
  setDirection("asc");
}

function compareRows(
  left: MonitorOverviewRow,
  right: MonitorOverviewRow,
  sortField: SortField,
  sortDirection: SortDirection,
) {
  const direction = sortDirection === "asc" ? 1 : -1;
  if (sortField === "type") {
    return (
      MonitorTypeUtil.toString(left.type).localeCompare(
        MonitorTypeUtil.toString(right.type),
        undefined,
        { sensitivity: "base" },
      ) * direction
    );
  }
  if (sortField === "recommendedVisits") {
    return (left.recommendedVisits - right.recommendedVisits) * direction;
  }
  if (sortField === "price") {
    return (left.price - right.price) * direction;
  }
  if (sortField === "updatedAt") {
    return compareTimestamps(left.updatedAt, right.updatedAt) * direction;
  }
  return (
    left.name.localeCompare(right.name, undefined, {
      sensitivity: "base",
    }) * direction
  );
}

function compareTimestamps(left?: string, right?: string): number {
  const leftTime = left ? Date.parse(left) : Number.MAX_SAFE_INTEGER;
  const rightTime = right ? Date.parse(right) : Number.MAX_SAFE_INTEGER;
  if (leftTime === rightTime) {
    return 0;
  }
  return leftTime < rightTime ? -1 : 1;
}

function formatDateTime(value?: string): string {
  if (!value) {
    return "-";
  }
  return `${formatDate(value)} ${formatTime(value)}`;
}

function formatPrice(value?: number): string {
  return (value ?? 0).toFixed(2);
}
